/**
 * Every route path in one place. Components import from here instead of writing
 * string literals, so a path change is one edit and a typo is a type error.
 */
export const paths = {
  home: '/',
  order: {
    root: '/order',
    upload: '/order/upload',
    review: '/order/review',
    options: '/order/options',
    details: '/order/details',
    address: '/order/address',
    pay: '/order/pay',
  },
  orderStatus: (orderId = ':orderId') => `/orders/${orderId}`,
  /**
   * The basket. Reached from the header icon on every page.
   *
   * Top-level rather than `/order/cart`: everything under `/order` is a step
   * in a linear flow with prerequisites in the URL, and the cart is the
   * opposite — no ids, reachable from anywhere, and it is where the flow is
   * re-entered from rather than a point along it.
   */
  cart: '/cart',
  /** Where a returning customer signs in. */
  signIn: '/signin',
  /** Everything this customer has bought. */
  orders: '/orders',
  /** Profile, saved addresses, sign out. Reached from the header icon. */
  account: '/account',
  /**
   * The published policy documents.
   *
   * Flat, top-level URLs rather than a `/legal/*` prefix: these get pasted into
   * a payment gateway's onboarding form, a marketplace listing and the odd
   * email, and `printoman.in/privacy` survives that better than a nested path.
   * The keys are the document slugs in features/legal/documents.ts, so
   * `paths.legal[slug]` is a total function over LegalSlug and the router can be
   * generated from the document list.
   */
  legal: {
    terms: '/terms',
    privacy: '/privacy',
    shipping: '/shipping-policy',
    refunds: '/refund-policy',
    returns: '/return-policy',
  },
} as const;

/**
 * Search-param names that carry checkout progress.
 *
 * These are part of the app's contract with the browser's back button and the
 * refresh key — see the persistence note in routes/order/steps.ts. Short names
 * because they end up in a URL a customer may see or share.
 */
export const searchParamKeys = {
  /** Upload id returned by POST /api/v1/uploads. */
  upload: 'u',
  /** Quote id returned by POST /api/v1/quotes. */
  quote: 'q',
  /** Order id returned by checkout. */
  order: 'o',
  /**
   * Copies chosen on the Options step.
   *
   * It is here rather than in component state for the same reason the ids are:
   * "3 copies" is progress the customer entered, and until Continue turns it
   * into a quote row the URL is the only place that survives a refresh. No step
   * `requires` it, so an absent or junk value simply falls back to 1.
   */
  copies: 'c',
  /**
   * Binding slug chosen on the Options step. Here for the same reason `copies`
   * is: it is progress the customer entered, and until Continue turns it into a
   * quote row the URL is the only place that survives a refresh. No step
   * `requires` it, so an absent or unknown value falls back to the catalogue
   * default ("no binding") — which is what the server does with it too.
   */
  binding: 'b',
  /**
   * Cover colour chosen on the Options step, for bindings that have a cover.
   * Same reasoning as `binding`: progress the customer entered, not yet on the
   * server. Ignored entirely when the chosen binding has no cover.
   */
  cover: 'k',
  /** Saved-address id chosen on the Address step, carried into checkout. */
  address: 'a',
} as const;
